import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { 
  BookOpen, ArrowRight, ArrowLeft, Sparkles, Heart, Shield, Sun, 
  CheckCircle, Star, Gem, Calendar, Users, ChevronLeft
} from 'lucide-react';
import { images } from '../../data/content';

const BookPage = () => {
  const { isRTL } = useLanguage();
  const location = useLocation();

  // Scroll to request section if hash is present
  useEffect(() => {
    if (location.hash === '#request') {
      const element = document.getElementById('request');
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  const content = {
    hero: {
      titleLine1: isRTL ? 'هل أنت إيجابي؟' : 'Are You a Positive Person?',
      titleLine2: isRTL ? 'كيف تصبح أحدهم' : 'How Can You Become One?',
      author: isRTL ? 'بقلم منى بزاري' : 'By Mona Bizzari',
      tagline: isRTL 
        ? 'الإيجابية ليست مزاجًا — إنها مهارة يمكنك بناؤها.'
        : "Positivity is not a mood — it's a skill you can build.",
      cta1: isRTL ? 'اطلب نسخة' : 'Request a Copy',
      cta2: isRTL ? 'استكشف برامج منى' : "View Mona's Programs"
    },
    description: {
      title: isRTL ? 'عن الكتاب' : 'About the Book',
      paragraphs: isRTL ? [
        'الإيجابية هي قدرتنا على التعرف على الفرص في خضم الصعوبات — وتحويل التحديات إلى لحظات من النمو والتعلم. إنها القوة التي تحفز التميز وتساعدنا على تحويل الفشل إلى خطوة للأمام.',
        'في هذا الكتاب، تدعوك منى بزاري في رحلة ملهمة وعملية لفهم ما تعنيه الإيجابية حقًا، وكيف تُبنى، وما الذي يحتاج إلى التغيير في التفكير والسلوك اليومي لتصبح جزءًا ثابتًا من حياتك.',
        'مكتوب مع تركيز قوي على التمكين والتطوير الشخصي، يقدم الكتاب رؤى واضحة وتأملات من الحياة الواقعية وخطوات عملية لإعادة تشكيل العقلية وبناء المرونة العاطفية وإحداث تغيير هادف ودائم — يوجه القراء نحو حياة أكثر قوة وإيجابية وإشباعًا.'
      ] : [
        'Positivity is our ability to recognize opportunities in the middle of difficulty — and to turn challenges into moments of growth and learning. It is the force that motivates excellence and helps us transform failure into a step forward.',
        'In this book, Mona Bizzari invites you on an inspiring, practical journey to understand what positivity truly means, how it is built, and what needs to change in everyday thinking and behavior for it to become a stable part of your life.',
        'Written with a strong focus on empowerment and personal development, the book offers clear insights, real-life reflections, and practical steps to reshape mindset, build emotional resilience, and create meaningful, lasting change — guiding readers toward a more empowered, positive, and fulfilling life.'
      ]
    },
    learnings: {
      title: isRTL ? 'ما ستتعلمه' : "What You'll Learn",
      items: isRTL ? [
        {
          title: 'الإيجابية كممارسة يومية',
          description: 'كيفية بناء الإيجابية كعادة مستدامة، وليس مجرد دفعة مؤقتة من الطاقة.',
          icon: Sparkles
        },
        {
          title: 'تحويل التحديات',
          description: 'تعلم كيفية رؤية الصعوبات كفرص للنمو والتحول الشخصي.',
          icon: Heart
        },
        {
          title: 'المرونة العاطفية',
          description: 'أدوات وتقنيات عملية لبناء وتقوية مرونتك العاطفية.',
          icon: Shield
        },
        {
          title: 'الثقة والقيادة الذاتية',
          description: 'كيفية تطوير الثقة الحقيقية وقيادة حياتك بوضوح وهدف.',
          icon: Sun
        }
      ] : [
        {
          title: 'Positivity as Daily Practice',
          description: 'How to build positivity as a sustainable habit, not just a temporary energy boost.',
          icon: Sparkles
        },
        {
          title: 'Transform Challenges',
          description: 'Learn to see difficulties as opportunities for growth and personal transformation.',
          icon: Heart
        },
        {
          title: 'Emotional Resilience',
          description: 'Practical tools and techniques to build and strengthen your emotional resilience.',
          icon: Shield
        },
        {
          title: 'Confidence & Self-Leadership',
          description: 'How to develop authentic confidence and lead your life with clarity and purpose.',
          icon: Sun
        }
      ]
    },
    quote: {
      text: isRTL 
        ? 'الإيجابية ليست تجاهل الواقع، بل هي اختيار كيفية الاستجابة له.'
        : 'Positivity is not ignoring reality, but choosing how to respond to it.',
      author: isRTL ? '— منى بزاري' : '— Mona Bizzari'
    },
    request: {
      title: isRTL ? 'اطلب نسختك' : 'Request Your Copy',
      description: isRTL 
        ? 'مهتم بالحصول على نسخة من "الإيجابية"؟ تواصل معنا وسنرد عليك بتفاصيل حول التوفر وخيارات الشراء.'
        : 'Interested in getting a copy of "Positivity"? Reach out to us and we\'ll get back to you with details on availability and purchase options.',
      cta: isRTL ? 'تواصل معنا' : 'Contact Us'
    },
    journey: {
      title: isRTL ? 'أكمل رحلة الشفاء' : 'Continue Your Healing Journey',
      description: isRTL 
        ? 'استكشف المزيد من الطرق للتواصل مع منى وتعميق رحلة التحول الخاصة بك.'
        : 'Explore more ways to connect with Mona and deepen your transformation journey.',
      links: [
        {
          title: isRTL ? 'جلسات شفاء الاحجار الكريمة' : 'Crystal Healing Sessions',
          description: isRTL ? 'جلسات شفاء فردية مصممة خصيصًا لك' : 'One-on-one healing sessions tailored for you',
          href: '/#crystal-healing',
          icon: Gem
        },
        {
          title: isRTL ? 'البرامج' : 'Programs',
          description: isRTL ? 'برامج جماعية وورش عمل للنمو' : 'Group programs and workshops for growth',
          href: '/#programs',
          icon: Users
        },
        {
          title: isRTL ? 'احجز جلسة' : 'Book a Session',
          description: isRTL ? 'سجل للبدء في رحلتك' : 'Apply to start your journey',
          href: '/#connect',
          icon: Calendar
        }
      ]
    },
    backToHome: isRTL ? 'العودة إلى الرئيسية' : 'Back to Home'
  };

  return (
    <div className="min-h-screen bg-charcoal">
      {/* Header Navigation */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-charcoal/95 backdrop-blur-md border-b border-white/10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-10">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <Link 
              to="/"
              className="flex items-center gap-2 text-cream-300 hover:text-gold transition-colors"
            >
              <ChevronLeft size={20} className={isRTL ? 'rotate-180' : ''} />
              <span className="text-sm uppercase tracking-wider">{content.backToHome}</span>
            </Link>
            <span className="font-script text-xl sm:text-2xl text-gold">Mona Bizzari</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative pt-28 sm:pt-36 pb-16 sm:pb-24 overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal via-charcoal-100/50 to-charcoal" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gold/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-gold/5 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-10 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Book Cover */}
            <div className={`${isRTL ? 'lg:order-2' : 'lg:order-1'}`}>
              <div className="relative max-w-sm mx-auto">
                {/* Glow effect */}
                <div className="absolute inset-0 bg-gold/15 blur-3xl rounded-3xl transform scale-90" />
                
                {/* Book cover image */}
                <div className="relative rounded-xl overflow-hidden shadow-2xl">
                  <img
                    src={images.book.positivity}
                    alt="Positivity Book by Mona Bizzari"
                    className="w-full h-auto object-cover"
                    loading="lazy"
                  />
                  {/* Subtle overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal/20 via-transparent to-transparent" />
                </div>
                
                {/* Shadow under book */}
                <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-3/4 h-4 bg-black/30 blur-xl rounded-full" />
              </div>
            </div>

            {/* Hero Content */}
            <div className={`${isRTL ? 'lg:order-1' : 'lg:order-2'} text-center lg:text-left`}>
              <p className="text-gold text-xs sm:text-sm uppercase tracking-[0.2em] sm:tracking-[0.3em] mb-4">
                {isRTL ? 'كتاب منى بزاري' : "Mona Bizzari's Book"}
              </p>
              
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl xl:text-6xl text-gold leading-tight mb-2">
                {content.hero.titleLine1}
              </h2>
              
              <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl xl:text-5xl text-gold/90 leading-tight mb-6">
                {content.hero.titleLine2}
              </h3>
              
              <p className="text-cream text-lg sm:text-xl lg:text-2xl italic mb-8 leading-relaxed">
                "{content.hero.tagline}"
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
                <a
                  href="#request"
                  className="btn-gold flex items-center justify-center gap-2 rounded"
                >
                  <BookOpen size={18} />
                  <span>{content.hero.cta1}</span>
                </a>
                <Link
                  to="/#programs"
                  className="btn-outline-gold flex items-center justify-center gap-2 rounded"
                >
                  <span>{content.hero.cta2}</span>
                  <ArrowRight size={16} className={isRTL ? 'rotate-180' : ''} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About the Book */}
      <section className="section-padding bg-charcoal-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10 sm:mb-12">
              <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-gold mb-4">
                {content.description.title}
              </h3>
              <div className="gold-divider" />
            </div>
            
            <div className="space-y-5 sm:space-y-6">
              {content.description.paragraphs.map((paragraph, index) => (
                <p 
                  key={index} 
                  className={`text-cream-300 leading-relaxed ${
                    index === 0 ? 'text-lg sm:text-xl lg:text-2xl text-cream font-light' : 'text-base sm:text-lg'
                  }`}
                >
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-charcoal relative overflow-hidden">
        <div className="absolute inset-0 bg-gold/5" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-10 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <blockquote className="font-serif text-xl sm:text-2xl lg:text-3xl xl:text-4xl text-gold italic leading-relaxed mb-6">
              "{content.quote.text}"
            </blockquote>
            <p className="text-cream-300 text-sm sm:text-base tracking-wider">
              {content.quote.author}
            </p>
          </div>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="section-padding bg-charcoal-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center mb-10 sm:mb-16">
            <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-gold mb-4">
              {content.learnings.title}
            </h3>
            <div className="gold-divider" />
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 max-w-4xl mx-auto">
            {content.learnings.items.map((item, index) => {
              const Icon = item.icon;
              return (
                <div 
                  key={index}
                  className="p-5 sm:p-6 lg:p-8 border border-white/10 bg-charcoal hover:border-gold/30 transition-all group rounded-lg"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center border border-gold/40 bg-gold/5 group-hover:bg-gold/10 transition-colors flex-shrink-0 rounded-lg">
                      <Icon size={24} className="text-gold" />
                    </div>
                    <div>
                      <h4 className="font-serif text-lg sm:text-xl text-gold mb-2">
                        {item.title}
                      </h4>
                      <p className="text-cream-300 text-sm sm:text-base leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Request a Copy */}
      <section id="request" className="section-padding bg-charcoal relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-6 border-2 border-gold/50 flex items-center justify-center rounded-full">
              <BookOpen size={32} className="text-gold sm:w-10 sm:h-10" />
            </div>
            
            <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-gold mb-4">
              {content.request.title}
            </h3>
            
            <div className="gold-divider mb-6 sm:mb-8" />
            
            <p className="text-cream-300 text-base sm:text-lg leading-relaxed mb-8">
              {content.request.description}
            </p>
            
            <Link
              to="/#connect"
              className="btn-gold inline-flex items-center gap-2 rounded"
            >
              <span>{content.request.cta}</span>
              <ArrowRight size={16} className={isRTL ? 'rotate-180' : ''} />
            </Link>
          </div>
        </div>
      </section>

      {/* Continue Your Journey */}
      <section className="section-padding bg-charcoal-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-10">
          <div className="text-center mb-10 sm:mb-12">
            <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-cream mb-4">
              {content.journey.title}
            </h3>
            <div className="gold-divider" />
            <p className="text-cream-300 mt-6 max-w-xl mx-auto text-sm sm:text-base">
              {content.journey.description}
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 max-w-4xl mx-auto">
            {content.journey.links.map((link, index) => {
              const Icon = link.icon;
              return (
                <Link
                  key={index}
                  to={link.href}
                  className="p-5 sm:p-6 border border-white/10 bg-charcoal hover:border-gold/40 transition-all group text-center rounded-lg"
                >
                  <div className="w-12 h-12 sm:w-14 sm:h-14 mx-auto mb-4 border border-gold/40 flex items-center justify-center group-hover:bg-gold/10 transition-colors rounded-lg">
                    <Icon size={24} className="text-gold" />
                  </div>
                  <h4 className="font-serif text-lg sm:text-xl text-gold mb-2">
                    {link.title}
                  </h4>
                  <p className="text-cream-400 text-sm">
                    {link.description}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-charcoal border-t border-white/10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-10 text-center">
          <Link to="/" className="font-script text-2xl text-gold mb-4 inline-block">
            Mona Bizzari
          </Link>
          <p className="text-cream-400 text-sm">
            {isRTL 
              ? '© 2026 منى بزاري. جميع الحقوق محفوظة.'
              : '© 2026 Mona Bizzari. All rights reserved.'}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BookPage;
